import { RegisterComponent } from '@/common/register-component';
import { NylasSchedulerConfigConnector } from '@/connector/nylas-scheduler-config-connector';
import { debug } from '@/utils/utils';
import { AttachInternals, Component, Element, Event, EventEmitter, Host, Listen, Prop, State, Watch, h } from '@stencil/core';
import { NylasSchedulerEditor } from '../nylas-scheduler-editor/nylas-scheduler-editor';
import { Configuration, EventReminder, GroupConfiguration, Participant, ThemeConfig } from '@nylas/core';
import i18next from '@/utils/i18n';
import { LANGUAGE_CODE } from '@/common/constants';

/**
 * The `nylas-reminder-emails` component is a UI component that allows users to set buffer time before and after an event.
 *
 * @part nre__tab-heading - The nylas-reminder-emails main tab heading
 * @part nre - The nylas-reminder-emails container
 * @part nre__header - The header of the event communication section
 * @part nre__body - The body of the event communication section
 * @part nre__summary - The reminder card summary container
 * @part nrt - The event reminder container
 * @part nrt__number-dropdown - The number dropdown container
 * @part nrt__number-dropdown-button - The number dropdown button
 * @part nrt__number-dropdown-content - The number dropdown content
 * @part nrt__period-dropdown - The period dropdown container
 * @part nrt__period-dropdown-button - The period dropdown button
 * @part nrt__period-dropdown-content - The period dropdown content
 * @part nrt__time-period-selector - The time period selector
 **/
@Component({
  tag: 'nylas-reminder-emails',
  styleUrl: 'nylas-reminder-emails.scss',
  shadow: true,
  formAssociated: true,
})
export class NylasReminderEmails {
  @Element() host!: HTMLNylasReminderEmailsElement;

  /**
   * The selected configuration.
   */
  @Prop() selectedConfiguration?: Configuration | GroupConfiguration;
  /**
   * The event reminders prop.
   */
  @Prop() eventReminders?: (EventReminder & { editing?: boolean })[];
  /**
   * The name of the component
   */
  @Prop() name: string = 'reminder-overrides';
  /**
   * The selected language.
   */
  @Prop() selectedLanguage?: LANGUAGE_CODE;
  /**
   * The theme configuration.
   */
  @Prop({ attribute: 'theme-config' }) readonly themeConfig?: ThemeConfig;
  /**
   * The element internals.
   */
  @AttachInternals() internals!: ElementInternals;

  /**
   * The event reminders state. Defaults to an empty array if no event reminders are present.
   */
  @State() reminders: (EventReminder & { editing?: boolean })[] =
    (this.selectedConfiguration as Configuration)?.event_booking?.reminders ??
    (this.selectedConfiguration as GroupConfiguration)?.group_booking?.reminders ??
    this.eventReminders ??
    [];
  @State() reminderErrors: {
    [key: string]: {
      error: string;
      suggestion: number;
    };
  } = {};
  @State() participants: Participant[] = (this.selectedConfiguration as Configuration)?.participants ?? [];

  /**
   * This event is fired when the email reminders change.
   */
  @Event() valueChanged!: EventEmitter<{
    value: string;
    name: string;
    valueChanged?: (event: CustomEvent<{ value: string; name: string }>) => void;
  }>;

  @State() clearError: boolean = false;

  @Event() nylasSuggestReminderMinutes!: EventEmitter<{ minutes: number }>;

  // Lifecycle methods
  connectedCallback() {
    debug('nylas-reminder-emails', 'connectedCallback');
    // Use queueMicrotask to defer theme application until after React has set props.
    // This fixes the timing issue where connectedCallback fires before React sets themeConfig.
    queueMicrotask(() => {
      if (this.themeConfig) {
        this.applyThemeConfig(this.themeConfig);
        this.host.setAttribute('data-themed', '');
      }
    });
  }

  disconnectedCallback() {
    debug('nylas-reminder-emails', 'disconnectedCallback');
  }

  componentWillLoad() {
    debug('nylas-reminder-emails', 'componentWillLoad');
    // See comment in the @Watch('name') decorator for more information.
    this.host.setAttribute('name', this.name);
  }

  componentDidLoad() {
    debug('nylas-reminder-emails', 'componentDidLoad');
    if (this.selectedConfiguration) {
      this.selectedConfigurationChangedHandler(this.selectedConfiguration);
    } else {
      this.reminders = this.eventReminders ?? [];
      this.updateRemindersFormValue(false);
    }
  }

  componentWillUpdate() {
    debug('nylas-reminder-emails', 'componentWillUpdate');
  }

  componentDidUpdate() {
    debug('nylas-reminder-emails', 'componentDidUpdate');
  }

  componentWillRender() {
    debug('nylas-reminder-emails', 'componentWillRender');
  }

  componentDidRender() {
    debug('nylas-reminder-emails', 'componentDidRender');
  }

  /**
   * When a name prop is passed, stencil does not automatically set the name attribute on the host element.
   * Since this component is form-associated, the name attribute is required for form submission.
   * This is a workaround to ensure that the name attribute is set on the host element.
   */
  @Watch('name')
  elementNameChangedHandler(newValue: string) {
    debug('nylas-reminder-emails', 'elementNameChangedHandler', newValue);
    this.host.setAttribute('name', newValue);
  }

  @Watch('selectedConfiguration')
  selectedConfigurationChangedHandler(newVal: Configuration | GroupConfiguration) {
    debug('nylas-reminder-emails', 'selectedConfigurationChangedHandler', newVal);
    const configuration = newVal as Configuration;
    const groupConfiguration = newVal as GroupConfiguration;
    this.reminders = configuration?.event_booking?.reminders ?? groupConfiguration?.group_booking?.reminders ?? [];
    this.participants = configuration?.participants ?? [];
    this.updateRemindersFormValue(false);
  }

  @Watch('selectedLanguage')
  selectedLanguageChangedHandler(newVal: LANGUAGE_CODE, oldVal: LANGUAGE_CODE) {
    if (newVal === oldVal) return;
    i18next.changeLanguage(newVal);
  }

  @Watch('themeConfig')
  themeConfigChangedHandler(newVal: ThemeConfig, oldVal: ThemeConfig) {
    if (newVal === oldVal) return;
    this.applyThemeConfig(newVal);
  }

  applyThemeConfig(themeConfig?: ThemeConfig) {
    if (themeConfig) {
      for (const [key, value] of Object.entries(themeConfig)) {
        this.host.style.setProperty(`${key}`, value);
      }
    }
  }

  @Listen('valueChanged')
  handleValueChanged(event: CustomEvent) {
    debug('nylas-reminder-emails', 'handleValueChanged', event);
    const { name, value } = event.detail;

    if (name.startsWith('reminder-time-')) {
      const remainder = parseInt(value) % 5;
      if (remainder !== 0) {
        this.reminderErrors[name] = {
          error: i18next.t('nylasReminderEmails.reminderMinutesValidationError'),
          suggestion: parseInt(value) + (5 - remainder),
        };
        this.reminderErrors = { ...this.reminderErrors };
        this.internals.setValidity({ customError: true }, 'Reminder time must be a multiple of 5');
        return;
      } else {
        delete this.reminderErrors[name];
        this.reminderErrors = { ...this.reminderErrors };
        this.internals.setValidity({ customError: false });
      }
      // Extract the reminder index from the name attribute (e.g. reminder-time-0)
      const [_, reminderIndex] = name.split(/(\d+)/);
      const updatedReminders = [...this.reminders];
      updatedReminders[reminderIndex].minutes_before_event = parseInt(value);
      this.reminders = [...updatedReminders];
      this.updateRemindersFormValue();
    }
  }

  @Listen('nylasFormDropdownChanged')
  handleNylasFormDropdownChanged(event: CustomEvent) {
    debug('nylas-reminder-emails', 'handleNylasFormDropdownChanged', event);
    const { name, value } = event.detail;
    if (!name.startsWith('recipient-')) {
      return;
    }
    // Extract the reminder index from the name attribute (e.g. recipient-0)
    const [_, reminderIndex] = name.split(/(\d+)/);
    const updatedReminders = [...this.reminders];
    updatedReminders[reminderIndex].recipient = value;
    this.reminders = [...updatedReminders];
    this.updateRemindersFormValue();
  }

  @Listen('nylasFormInputChanged')
  handleNylasFormInputChanged(event: CustomEvent) {
    debug('nylas-reminder-emails', 'handleNylasFormInputChanged', event);
    const { name, value } = event.detail;
    if (!name.startsWith('reminder-subject-')) {
      return;
    }
    // Extract the reminder index from the name attribute (e.g. reminder-subject-0)
    const [_, reminderIndex] = name.split(/(\d+)/);
    const updatedReminders = [...this.reminders];
    updatedReminders[reminderIndex].email_subject = value;
    this.reminders = [...updatedReminders];
    this.updateRemindersFormValue();
  }

  addReminder() {
    debug('nylas-reminder-emails', 'addReminder');
    const recipient = this.selectedConfiguration?.type === 'group' ? 'guest' : 'all';
    this.reminders = [
      ...this.reminders,
      {
        type: 'email',
        minutes_before_event: 30,
        editing: true,
        recipient: recipient,
      },
    ];
    this.updateRemindersFormValue();
  }

  deleteReminder(index: number) {
    this.reminders = this.reminders.filter((_, i) => i !== index).map(p => ({ ...p }));
    this.updateRemindersFormValue();
  }

  get isInternalsAvailable() {
    return this.internals !== undefined && typeof this.internals.setFormValue === 'function';
  }

  updateRemindersFormValue(valueChanged: boolean = true) {
    debug('nylas-reminder-emails', 'updateRemindersFormValue');
    const eventReminders = this.reminders.map(reminder => {
      const rem = { ...reminder };
      rem?.editing && delete rem.editing;
      return rem;
    });
    if (valueChanged) {
      this.valueChanged.emit({
        value: JSON.stringify(eventReminders),
        name: this.name,
      });
    }
    this.isInternalsAvailable && this.internals.setFormValue(JSON.stringify(eventReminders), this.name);
  }

  formatReminderTime(reminderTime: number) {
    switch (true) {
      case reminderTime > 24 * 60:
        return `${(reminderTime / 60 / 24).toFixed(2)} ${i18next.t('time.days')}`;
      case reminderTime === 24 * 60:
        return `1 ${i18next.t('time.days')}`;
      case reminderTime > 90 || reminderTime === 60:
        return `${(reminderTime / 60).toFixed(2)} ${i18next.t('time.hours')}`;
      default:
        return `${reminderTime} ${reminderTime > 1 ? i18next.t('time.minutes') : i18next.t('time.minute')} `;
    }
  }

  @RegisterComponent<NylasReminderEmails, NylasSchedulerConfigConnector, Exclude<NylasSchedulerEditor['stores'], undefined>>({
    name: 'nylas-reminder-emails',
    stateToProps: new Map([
      ['schedulerConfig.selectedConfiguration', 'selectedConfiguration'],
      ['schedulerConfig.selectedLanguage', 'selectedLanguage'],
      ['schedulerConfig.themeConfig', 'themeConfig'],
    ]),
    eventToProps: {
      valueChanged: async (
        event: CustomEvent<{ value: string; name: string; valueChanged?: (event: CustomEvent<{ value: string; name: string }>) => void }>,
        _nylasSchedulerConfigConnector: NylasSchedulerConfigConnector,
      ) => {
        const { valueChanged } = event.detail;
        // If a handler is passed, call it.
        if (valueChanged) {
          valueChanged(event);
        }
      },
    },
    fireRegisterEvent: true,
  })
  render() {
    const organizerName = this.participants.find(p => p.is_organizer)?.name;
    let recipientOptions = [
      {
        label: i18next.t('nylasReminderEmails.recipientOptions', { context: 'all' }),
        value: 'all',
      },
      {
        label: i18next.t('nylasReminderEmails.recipientOptions', { context: 'guest' }),
        value: 'guest',
      },
      {
        label: i18next.t('nylasReminderEmails.recipientOptions', { context: 'host' }),
        value: 'host',
      },
    ];
    if (this.selectedConfiguration?.type === 'group') {
      recipientOptions = recipientOptions.filter(option => option.value !== 'all');
    }

    return (
      <Host>
        <div class="cards-container">
          {this.reminders.map((reminder, index) => {
            return reminder.type !== 'webhook' ? (
              <div class="nylas-reminder-email" part="nre">
                <div class="header" part="nre__header">
                  <div>
                    <h3>
                      {organizerName
                        ? i18next.t('nylasReminderEmails.headerTitle_withOrganizer', { organizerName: ` ${organizerName}` })
                        : i18next.t('nylasReminderEmails.headerTitle')}
                    </h3>
                    <p>{i18next.t('nylasReminderEmails.headerSubTitle')}</p>
                  </div>
                  {!reminder.editing ? (
                    <button-component
                      variant={'basic'}
                      key={`edit-${index} `}
                      clickHandler={event => {
                        event.preventDefault();
                        reminder.editing = true;
                        this.reminders = [...this.reminders];
                      }}
                    >
                      <edit-icon width="16" height="16"></edit-icon>
                      {i18next.t('nylasReminderEmails.editButtonText')}
                    </button-component>
                  ) : (
                    <div class="reminder-open-close-toggle">
                      <button-component
                        variant={'basic'}
                        class="delete-btn"
                        tooltip="Delete reminder"
                        key={`delete -${index} `}
                        clickHandler={event => {
                          event.preventDefault();
                          this.deleteReminder(index);
                        }}
                      >
                        <trash-icon width="16" height="16"></trash-icon>
                      </button-component>
                      <span
                        class={`chevron ${reminder.editing ? 'open' : 'closed'}`}
                        onClick={() => {
                          reminder.editing = !reminder.editing;
                          this.reminders = [...this.reminders];
                        }}
                      >
                        <chevron-icon width="24" height="24" />
                      </span>
                    </div>
                  )}
                </div>

                {reminder.editing ? (
                  <div class="nylas-reminder-email__body" part="nre__body">
                    <div class="nylas-reminder-email__section">
                      <div class="nylas-reminder-email__row">
                        <label>{i18next.t('nylasReminderEmails.emailRecipients.label')}</label>
                        <select-dropdown
                          name={`recipient-${index}`}
                          options={recipientOptions}
                          defaultSelectedOption={recipientOptions.find(i => i.value === reminder.recipient) ?? recipientOptions[0]}
                          withSearch={false}
                          themeConfig={this.themeConfig}
                        />
                      </div>
                      <div class="nylas-reminder-email__row">
                        <label>
                          {i18next.t('nylasReminderEmails.sendConfirmation.label')}
                          <span class="label-icon">
                            <tooltip-component>
                              <info-icon slot="tooltip-icon" />
                              <span slot="tooltip-content">{i18next.t('nylasReminderEmails.sendConfirmation.tooltip.desc')}</span>
                            </tooltip-component>
                          </span>
                        </label>
                        <div>
                          <nylas-reminder-time
                            name={`reminder-time-${index}`}
                            eventReminderTimeMinutes={reminder.minutes_before_event}
                            themeConfig={this.themeConfig}
                            exportparts="nrt, nrt__number-dropdown, nrt__number-dropdown-button, nrt__number-dropdown-content, nrt__period-dropdown, nrt__period-dropdown-button, nrt__period-dropdown-content, nrt__time-period-selector"
                          />
                          {this.reminderErrors[`reminder-time-${index}`]?.error && (
                            <p class="error-message">
                              {this.reminderErrors[`reminder-time-${index}`]?.error}{' '}
                              <button-component
                                variant="invisible"
                                onClick={_e => {
                                  // this.clearError = !this.clearError;
                                  // this.handleValueChanged({ detail: { name: `reminder-time-${index}`, value: this.reminderErrors[`reminder-time-${index}`]?.suggestion } } as CustomEvent);
                                  this.nylasSuggestReminderMinutes.emit({ minutes: this.reminderErrors[`reminder-time-${index}`]?.suggestion });
                                }}
                              >
                                {this.reminderErrors[`reminder-time-${index}`]?.suggestion}
                              </button-component>
                            </p>
                          )}
                        </div>
                      </div>
                      <div class="nylas-reminder-email__block">
                        <label>{i18next.t('nylasReminderEmails.emailSubject.label')}</label>
                        <input-component
                          name={`reminder-subject-${index}`}
                          maxLength={60}
                          placeholder={
                            organizerName
                              ? i18next.t('nylasReminderEmails.emailSubject.placeholder_withOrganizer', { organizerName: organizerName })
                              : i18next.t('nylasReminderEmails.emailSubject.placeholder')
                          }
                          defaultValue={reminder?.email_subject || ''}
                          themeConfig={this.themeConfig}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div class="nylas-reminder-email__summary" part="nre__summary">
                    <p>
                      <span class="summary-icon">
                        <person-clipboard-icon></person-clipboard-icon>
                      </span>
                      {recipientOptions.find(i => i.value === reminder.recipient)?.label}
                    </p>
                    <p>
                      <span class="summary-icon">
                        <clock-icon></clock-icon>
                      </span>
                      {this.formatReminderTime(reminder.minutes_before_event)} {i18next.t('nylasReminderEmails.beforeTheEvent')}
                    </p>
                  </div>
                )}
              </div>
            ) : null;
          })}
        </div>

        <button-component
          class="create-reminder"
          variant={'basic'}
          clickHandler={event => {
            event.preventDefault();
            this.addReminder();
          }}
        >
          <plus-icon width="16" height="16"></plus-icon>
          {i18next.t('nylasReminderEmails.newReminderButtonText')}
        </button-component>
      </Host>
    );
  }
}
